/**
 * SuperAdmin Finance — Platform MRR + Payout Ledger
 * Reads: GET /api/superadmin/finance
 * Displays: 3 KPI cards, MRR by plan table, recent payouts table (last 20).
 *
 * All hex values match the established SuperAdmin web palette
 * (colours sourced from mobile/src/styles/colors.js design tokens).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiDollarSign, FiTrendingUp, FiPackage, FiCheckCircle, FiTag, FiShare2, FiDownload } from 'react-icons/fi';
import { getSuperAdminFinance, getPaymentAnalyticsPlatform, getPackageStatsPlatform } from '../../utils/api';

// SuperAdmin web palette — heading/card accents (#1a1a2e, #1a6b5a) inherited from
// SuperAdminDashboard.js shell. Pending Sprint C hex audit (same as Overview.js etc.).
// Status badge palette — hex values match colors.js design tokens.
const STATUS_COLOURS = {
  pending:  { bg: 'rgba(230,126,34,0.1)', color: '#E67E22' },
  approved: { bg: '#EBF5FB',              color: '#2980B9' },
  paid:     { bg: '#EAF7EF',              color: '#27AE60' },
  rejected: { bg: '#FDEDEC',              color: '#E74C3C' },
};

function formatINR(n) {
  if (typeof n !== 'number') return '₹0';
  return '₹' + n.toLocaleString('en-IN');
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const S = {
  wrap:    { padding: '0' },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' },
  heading: { fontSize: '16px', fontWeight: '700', color: '#1a1a2e', margin: 0 },

  refreshBtn: (loading) => ({
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '8px 14px', borderRadius: '8px',
    border: '1px solid #e8eae8', background: '#fff',
    cursor: loading ? 'default' : 'pointer',
    fontSize: '13px', fontWeight: '600', color: '#1a6b5a',
    opacity: loading ? 0.6 : 1, minHeight: '44px',
  }),

  kpiRow:  { display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' },
  kpiCard: (accent) => ({
    flex: '1 1 180px', background: '#fff', borderRadius: '12px',
    border: '1px solid #e8eae8', borderTop: `3px solid ${accent}`,
    padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  }),
  kpiLabel: { fontSize: '12px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  kpiValue: { fontSize: '26px', fontWeight: '800', color: '#1a1a2e', lineHeight: 1.1 },
  kpiSub:   { fontSize: '12px', color: '#888', marginTop: '4px' },

  section:  { marginBottom: '28px' },
  secTitle: { fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginBottom: '12px' },

  table:   { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e8eae8' },
  th:      { fontSize: '12px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '0.4px', padding: '12px 16px', textAlign: 'left', background: '#f9fafb', borderBottom: '1px solid #e8eae8' },
  thRight: { textAlign: 'right' },
  td:      { fontSize: '13px', color: '#1a1a2e', padding: '12px 16px', borderBottom: '1px solid #f4f6f4', verticalAlign: 'middle' },
  tdRight: { textAlign: 'right' },
  tdMuted: { color: '#888' },

  tfootRow: { background: 'rgba(26,107,90,0.04)' },
  tdBold:   { fontWeight: '700' },

  badge: (status) => {
    const c = STATUS_COLOURS[status] || STATUS_COLOURS.pending;
    return { display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', background: c.bg, color: c.color };
  },

  empty:   { padding: '28px 16px', textAlign: 'center', color: '#888', fontSize: '13px' },
  error:   { padding: '16px', background: '#FDEDEC', borderRadius: '8px', color: '#E74C3C', fontSize: '13px', marginBottom: '20px' },
  loading: { padding: '48px', textAlign: 'center', color: '#888', fontSize: '14px' },

  select:    { padding: '8px 12px', borderRadius: '8px', border: '1px solid #e8eae8',
               fontSize: '13px', color: '#1a1a2e', background: '#fff', minHeight: '44px' },
  exportBtn: (busy) => ({
               display: 'flex', alignItems: 'center',
               padding: '8px 18px', borderRadius: '8px', border: 'none',
               background: busy ? '#9CA3AF' : '#1a6b5a', color: '#fff',
               fontSize: '13px', fontWeight: '600', cursor: busy ? 'not-allowed' : 'pointer',
               minHeight: '44px' }),
};

const Finance = () => {
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [payAnalytics, setPayAnalytics] = useState(null);
  const [pkgStats,     setPkgStats]     = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const [finRes, payRes, pkgRes] = await Promise.allSettled([
        getSuperAdminFinance(),
        getPaymentAnalyticsPlatform(month),
        getPackageStatsPlatform(month),
      ]);
      if (finRes.status === 'fulfilled') setData(finRes.value.data.data);
      else setError(finRes.reason?.response?.data?.message || 'Failed to load finance data');
      if (payRes.status === 'fulfilled') setPayAnalytics(payRes.value.data.data);
      if (pkgRes.status === 'fulfilled') setPkgStats(pkgRes.value.data.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [filterType,      setFilterType]      = useState('month');
  const [selectedMonth,   setSelectedMonth]   = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedQuarter, setSelectedQuarter] = useState(() => {
    const q = Math.ceil((new Date().getMonth() + 1) / 3);
    return `Q${q}-${new Date().getFullYear()}`;
  });
  const [exporting, setExporting] = useState(false);

  const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const QUARTER_OPTIONS = (() => {
    const opts = [];
    let y = new Date().getFullYear();
    let q = Math.ceil((new Date().getMonth() + 1) / 3);
    for (let i = 0; i < 8; i++) {
      opts.push(`Q${q}-${y}`);
      q--; if (q < 1) { q = 4; y--; }
    }
    return opts;
  })();

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const param  = filterType === 'month' ? `month=${selectedMonth}` : `quarter=${selectedQuarter}`;
      const token  = sessionStorage.getItem('pwc_token') || localStorage.getItem('pwc_token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const res    = await fetch(`${apiUrl}/api/superadmin/invoices/export?${param}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { alert('Export failed. Please retry.'); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `LifeChiAI-GST-${filterType === 'month' ? selectedMonth : selectedQuarter}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[Finance] GST export failed:', err?.message);
      alert('Export failed. Please retry.');
    } finally { setExporting(false); }
  };

  if (loading && !data) return <div style={S.loading}>Loading finance data…</div>;

  const planTotal = data?.mrrByPlan?.reduce((s, r) => s + r.count, 0) || 0;
  const mrrTotal  = data?.mrrByPlan?.reduce((s, r) => s + r.mrr,   0) || 0;

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <h2 style={S.heading}>Finance</h2>
        <button style={S.refreshBtn(loading)} onClick={fetchData} disabled={loading} type="button">
          <FiRefreshCw size={14} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div style={S.error}>{error}</div>}

      {/* KPI cards */}
      <div style={S.kpiRow}>
        <div style={S.kpiCard('#1a6b5a')}>
          <div style={S.kpiLabel}>Platform MRR</div>
          <div style={S.kpiValue}>{formatINR(data?.platformMRR)}</div>
          <div style={S.kpiSub}>Monthly recurring revenue</div>
        </div>
        <div style={S.kpiCard('#E67E22')}>
          <div style={S.kpiLabel}>Pending payouts</div>
          <div style={S.kpiValue}>{data?.pendingPayouts ?? '—'}</div>
          <div style={S.kpiSub}>Awaiting admin approval</div>
        </div>
        <div style={S.kpiCard('#27AE60')}>
          <div style={S.kpiLabel}>Paid this month</div>
          <div style={S.kpiValue}>{formatINR(data?.totalPayoutsThisMonth)}</div>
          <div style={S.kpiSub}>Approved + disbursed</div>
        </div>
        <div style={S.kpiCard('#2980B9')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <FiDollarSign size={14} color='#888' />
            <div style={S.kpiLabel}>Payments collected</div>
          </div>
          <div style={S.kpiValue}>{payAnalytics ? formatINR(Math.round(payAnalytics.totalPlatformRevenue / 100)) : '—'}</div>
          <div style={S.kpiSub}>All centres this month</div>
        </div>
        <div style={S.kpiCard('#27AE60')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <FiTrendingUp size={14} color='#888' />
            <div style={S.kpiLabel}>Conversion rate</div>
          </div>
          <div style={S.kpiValue}>{payAnalytics ? `${payAnalytics.conversionRate}%` : '—'}</div>
          <div style={S.kpiSub}>Paid / total requests</div>
        </div>
        <div style={S.kpiCard('#9B59B6')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <FiPackage size={14} color='#888' />
            <div style={S.kpiLabel}>Avg package value</div>
          </div>
          <div style={S.kpiValue}>{payAnalytics ? formatINR(Math.round(payAnalytics.averagePackageValue / 100)) : '—'}</div>
          <div style={S.kpiSub}>{payAnalytics?.topCentreByRevenue ? `Top: ${payAnalytics.topCentreByRevenue.centreName}` : 'This month'}</div>
        </div>
        <div style={S.kpiCard('#27AE60')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <FiCheckCircle size={14} color='#888' />
            <div style={S.kpiLabel}>Package completion</div>
          </div>
          <div style={S.kpiValue}>{pkgStats ? `${pkgStats.packageCompletionRate}%` : '—'}</div>
          <div style={S.kpiSub}>Completed packages this month</div>
        </div>
        <div style={S.kpiCard('#9B59B6')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <FiTag size={14} color='#888' />
            <div style={S.kpiLabel}>Total discount given</div>
          </div>
          <div style={S.kpiValue}>{pkgStats ? formatINR(Math.round(pkgStats.promoPerformance.totalDiscountGiven / 100)) : '—'}</div>
          <div style={S.kpiSub}>{pkgStats?.promoPerformance.topPromo ? `Top: ${pkgStats.promoPerformance.topPromo.code}` : 'Promo discounts this month'}</div>
        </div>
        <div style={S.kpiCard('#2980B9')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <FiShare2 size={14} color='#888' />
            <div style={S.kpiLabel}>Referral conversion</div>
          </div>
          <div style={S.kpiValue}>{pkgStats ? `${pkgStats.referralConversion.conversionRate}%` : '—'}</div>
          <div style={S.kpiSub}>{pkgStats ? `${pkgStats.referralConversion.approved} / ${pkgStats.referralConversion.clicked} referrals` : 'Clicked → Approved'}</div>
        </div>
      </div>

      {/* GST XLS Export */}
      <div style={S.section}>
        <div style={S.secTitle}>GST Invoice Export</div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={S.select}>
            <option value="month">Month</option>
            <option value="quarter">Quarter</option>
          </select>
          {filterType === 'month' ? (
            <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} style={S.select}>
              {MONTH_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          ) : (
            <select value={selectedQuarter} onChange={e => setSelectedQuarter(e.target.value)} style={S.select}>
              {QUARTER_OPTIONS.map(q => <option key={q} value={q}>{q}</option>)}
            </select>
          )}
          <button onClick={handleExport} disabled={exporting} style={S.exportBtn(exporting)} type="button">
            <FiDownload size={14} style={{ marginRight: 6 }} />
            {exporting ? 'Exporting…' : 'Export GST XLS'}
          </button>
        </div>
      </div>

      {/* MRR by plan */}
      <div style={S.section}>
        <div style={S.secTitle}>MRR by plan</div>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Plan</th>
              <th style={{ ...S.th, ...S.thRight }}>Centres</th>
              <th style={{ ...S.th, ...S.thRight }}>Monthly revenue</th>
            </tr>
          </thead>
          <tbody>
            {data?.mrrByPlan?.length > 0 ? data.mrrByPlan.map(row => (
              <tr key={row.plan}>
                <td style={S.td}>{row.plan}</td>
                <td style={{ ...S.td, ...S.tdRight }}>{row.count}</td>
                <td style={{ ...S.td, ...S.tdRight }}>{formatINR(row.mrr)}</td>
              </tr>
            )) : (
              <tr><td colSpan={3} style={S.empty}>No plan data available</td></tr>
            )}
          </tbody>
          {data?.mrrByPlan?.length > 0 && (
            <tfoot>
              <tr style={S.tfootRow}>
                <td style={{ ...S.td, ...S.tdBold }}>Total</td>
                <td style={{ ...S.td, ...S.tdRight, ...S.tdBold }}>{planTotal}</td>
                <td style={{ ...S.td, ...S.tdRight, ...S.tdBold }}>{formatINR(mrrTotal)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Recent payouts */}
      <div style={S.section}>
        <div style={S.secTitle}>
          Recent payouts <span style={S.tdMuted}>(last 20)</span>
        </div>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Centre</th>
              <th style={S.th}>Healer</th>
              <th style={{ ...S.th, ...S.thRight }}>Amount</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Date</th>
            </tr>
          </thead>
          <tbody>
            {data?.recentPayouts?.length > 0 ? data.recentPayouts.map((p) => (
              <tr key={String(p.id)}>
                <td style={S.td}>{p.tenantName}</td>
                <td style={S.td}>{p.healerName}</td>
                <td style={{ ...S.td, ...S.tdRight }}>{formatINR(p.amount)}</td>
                <td style={S.td}><span style={S.badge(p.status)}>{p.status}</span></td>
                <td style={{ ...S.td, ...S.tdMuted }}>{formatDate(p.createdAt)}</td>
              </tr>
            )) : (
              <tr><td colSpan={5} style={S.empty}>No payouts recorded yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Finance;
